import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { LANGUAGES, i18n as appI18n } from '@muzly/shared/i18n';

import { queueProfileSync } from '@muzly/api-client';
import { useGoBack } from '@muzly/shared';

import { ListCard, PageHeader } from '../../components';
import { BottomCta } from '../../layouts';

/**
 * 언어 선택(Figma `MY #언어`) — MY 의 `언어` 줄에서 들어온다.
 *
 * **고르는 것은 초안이고 `완료` 가 반영한다.** 라디오를 누르는 순간 화면 언어가 바뀌면
 * 방금 읽던 안내문과 버튼이 다른 말로 바뀌어, 잘못 누른 사람은 돌아갈 줄을 못 읽는다.
 * 뒤로가기는 아무것도 바꾸지 않는다.
 *
 * 반영은 서버 큐에도 싣는다. 부팅마다 `RequireProfile` 이 서버 프로필의 `lang` 으로 언어를
 * 맞추므로, 로컬만 바꾸면 다음 실행에 되돌아간다. 같은 언어로 `완료` 를 누르면 보낼 것이 없다.
 *
 * 언어 이름은 **지금 화면 언어로** 적는다(시안: 한국어 · 영어). 온보딩의 선택지는 자국어
 * 표기(`web.language.*`)를 쓴다 — 거기서는 아직 읽을 수 있는 언어를 모른다.
 */
export const LanguagePage = () => {
  const { t, i18n } = useTranslation();
  const goBack = useGoBack();
  const groupId = useId();
  const current = i18n.resolvedLanguage ?? i18n.language;
  // 고르기 전에는 지금 언어를 따라간다 — 이 화면에서 콜드 부팅하면 `RequireProfile` 이
  // 서버 `lang` 으로 언어를 뒤늦게 맞추는데, 마운트 때 값을 잡아 두면 손대지 않은 `완료` 가
  // 옛 언어로 되돌린다.
  const [picked, setPicked] = useState<string | null>(null);
  const selected = picked ?? current;
  const [applying, setApplying] = useState(false);

  const done = async (): Promise<void> => {
    // 나가는 동안 다시 눌리면 히스토리를 두 칸 뺀다 — 두 경로 모두 먼저 잠근다.
    setApplying(true);
    if (selected === current) {
      void goBack();
      return;
    }
    await i18n.changeLanguage(selected);
    // `changeLanguage` 는 실패해도 reject 하지 않는다(i18next 25: 늘 resolve). 그 사이 다른
    // 전환이 끼어들면 언어가 안 바뀐 채 돌아온다 — 바뀐 것을 보고 나서 큐에 싣는다.
    // 훅이 준 `i18n` 은 렌더 시점의 사본(react-i18next 16 의 wrapper)이라 여기서는 옛 값이다 —
    // 앱 인스턴스에서 읽는다.
    if ((appI18n.resolvedLanguage ?? appI18n.language) !== selected) {
      setApplying(false);
      return;
    }
    queueProfileSync({ lang: selected });
    void goBack();
  };

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader title={t('web.more.language.title')} />

      <div className="flex flex-1 flex-col gap-4 px-[18px] pb-8 pt-5">
        <p
          id={groupId}
          className="px-1 text-[15px] leading-[1.4] tracking-[-0.075px] text-bk-700"
        >
          {t('web.more.language.desc')}
        </p>
        <ListCard role="radiogroup" aria-labelledby={groupId}>
          {LANGUAGES.map((lng) => {
            const checked = selected === lng;
            return (
              <label
                key={lng}
                className="flex cursor-pointer items-center py-[18px]"
              >
                <input
                  type="radio"
                  name={groupId}
                  value={lng}
                  checked={checked}
                  onChange={() => setPicked(lng)}
                  className="peer sr-only"
                />
                <span
                  className={cn(
                    'min-w-0 flex-1 text-base leading-[1.4] tracking-[-0.08px] text-bk-900',
                    checked && 'font-medium',
                  )}
                >
                  {t(`web.more.language.options.${lng}`)}
                </span>
                {/* 22px 라디오 — 선택은 빨간 링 + 점, 미선택은 회색 링. */}
                {/* 포커스 링은 `ring` 으로 그린다 — `outline` + `outline-2` 를 `cn` 에 같이 넣으면
                    twMerge 가 앞의 것을 지워 outline-style 이 none 으로 남는다. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-white peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2',
                    checked ? 'border-primary' : 'border-bk-400',
                  )}
                >
                  {checked && (
                    <span className="h-3 w-3 rounded-full bg-primary" />
                  )}
                </span>
              </label>
            );
          })}
        </ListCard>
      </div>

      <BottomCta onClick={() => void done()} disabled={applying}>
        {t('web.more.language.done')}
      </BottomCta>
    </div>
  );
};
